"use client";

import React from "react";
import { ArrowLeft, ExternalLink, Image as ImageIcon } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";

function SingleRecentWorkConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 animate-pulse">
        <div className="mb-8 h-10 w-32 bg-muted rounded-md"></div>
        <div className="mb-6 h-12 w-3/4 bg-muted rounded-md"></div>
        <div className="mb-10 aspect-video w-full bg-muted rounded-xl"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-5/6 bg-muted rounded-md"></div>
          <div className="h-4 w-4/6 bg-muted rounded-md"></div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto max-w-4xl px-4 text-center py-20">
        <h2 className="text-2xl font-bold text-foreground mb-4">Project Not Found</h2>
        <p className="text-muted-foreground mb-8">The recent work you are looking for does not exist or could not be loaded.</p>
        <Link to="/OurWorkPage">
          <Button>Return to Our Work</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const customFields = post.customFields || {};
  
  
  const displayImage = post.featuredImage || customFields.image;
  const displayTitle = post.title || customFields.title || "Recent Work Project";
  const displayDescription = customFields.description || post.content || post.excerpt;

  return (
    <div className="container mx-auto max-w-4xl px-4">
      {}
      <div className="mb-8">
        <Link to="/OurWorkPage">
          <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-primary text-muted-foreground transition-colors">
            <ArrowLeft className="mr-2 size-4" />
            Back to Our Work
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-8">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-4">
          {displayTitle}
        </h1>
        <Separator className="w-24 h-1 bg-primary rounded-full" />
      </header>

      {}
      <div className="mb-12 rounded-xl overflow-hidden shadow-xl border border-border bg-muted/30">
        <AspectRatio ratio={16 / 9}>
          {displayImage ? (
            <img
              src={displayImage}
              alt={displayTitle}
              className="object-cover w-full h-full transition-transform duration-700 hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
              <ImageIcon className="size-16 mb-4 opacity-20" />
              <span className="text-sm font-medium">No image available</span>
            </div>
          )}
        </AspectRatio>
      </div>

      {}
      <div className="bg-card border border-border rounded-xl p-6 md:p-10 shadow-sm mb-12">
        <h2 className="text-2xl font-semibold text-foreground mb-6">Project Details</h2>
        {displayDescription ? (
          <div className="prose prose-lg max-w-none text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {displayDescription}
          </div>
        ) : (
          <p className="text-muted-foreground italic">No detailed description provided for this project.</p>
        )}
      </div>

      {}
      {customFields.project_url && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-secondary text-secondary-foreground rounded-xl p-8 shadow-md">
          <div className="mb-6 sm:mb-0 text-center sm:text-left">
            <h3 className="text-xl font-bold mb-2">View the Live Project</h3>
            <p className="text-secondary-foreground/80 text-sm">
              See the results of our work firsthand.
            </p>
          </div>
          <Link to={customFields.project_url} newTab>
            <Button size="lg" className="gap-2 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              Visit Project Website
              <ExternalLink className="size-4" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function SingleRecentWorkTemplateSection() {
  
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: "recent_work" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-recent-work-template-section" className="bg-background py-16 md:py-24 lg:py-32">
      <PostsProvider wp_query={wp_query}>
        <SingleRecentWorkConsumer />
      </PostsProvider>
    </section>
  );
}